"use client"
import {UiErrorText} from "@/components/i18n/ui-error-text"
import type { ReactNode } from "react"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ShopifyLogo } from "@/components/ui/brand-logos"
import { useSocialAuth } from "@/hooks/auth/use-social-auth"
import { cn } from "@/lib/utils"
import { getShopifyAppListingUrl } from "@/lib/shopify/constants/app-listing-url"
import { useTranslations } from "next-intl"



type SocialDividerPlacement = "before" | "after" | "none"
type SocialButtonVariant = "icon" | "prominent"
/** Sign-in opens the installed app, whose app-open signs the owner in; install opens the App Store listing. */
type ShopifyLinkTarget = "sign-in" | "install"

const PROMINENT_ROW_CLASS = "grid auto-cols-fr grid-flow-col gap-2 sm:gap-3"

export function SocialSignInButtons({
  rememberMe = false,
  returnTo = "/",
  dividerPlacement = "after",
  variant = "icon",
  headingLeading,
  shopify,
}: {
  rememberMe?: boolean
  returnTo?: string
  dividerPlacement?: SocialDividerPlacement
  variant?: SocialButtonVariant
  headingLeading?: ReactNode
  shopify?: ShopifyLinkTarget
}) {

  const tUi = useTranslations("Auth.SocialSignInButtons")

  const { options, start } = useSocialAuth()
  if (options.isLoading) {
    // Only the install link is known before the options load.
    const placeholders = shopify === "install" && getShopifyAppListingUrl() ? 3 : 2
    return <SocialSignInButtonsLoader dividerPlacement={dividerPlacement} prominent={variant === "prominent"} headingLeading={headingLeading} placeholders={placeholders} />
  }
  const providers = options.data?.providers ?? []
  const shopifyUrl = resolveShopifyUrl(shopify, options.data?.shopifyAppUrl ?? null)
  if (!providers.length && !shopifyUrl) return null
  const prominent = variant === "prominent"
  // Three "Continue with …" labels do not fit one row.
  const fullLabels = providers.length + (shopifyUrl ? 1 : 0) < 3
  const buttonClass = prominent
    ? cn("h-10 w-auto min-w-0 flex-1 gap-1 rounded-md px-2 sm:h-12 sm:gap-2", fullLabels && "sm:px-4")
    : "size-11 rounded-full"
  return <div className="space-y-3">
    {dividerPlacement === "before" ? <SocialDivider label={tUi("orContinueWith")} /> : null}
    {prominent ? <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
      <div>{headingLeading}</div>
      <p className="text-center text-sm font-medium"><span key={tUi("signUpFasterWith")}>{tUi("signUpFasterWith")}</span></p>
      <div />
    </div> : null}
    <div className={prominent ? PROMINENT_ROW_CLASS : "flex justify-center gap-3"}>
      {providers.map(provider => <Button key={provider} type="button" variant="outline" size="icon" className={buttonClass} disabled={start.isPending}
        aria-label={tUi("continueWithValue0", { value0: String(socialProviderName(provider)) })}
        onClick={() => start.mutate({ provider, rememberMe, returnTo })}>
        <SocialProviderLogo provider={provider} />
        {prominent ? <ProminentLabel name={socialProviderName(provider)} full={fullLabels} /> : null}
      </Button>)}
      {/* A link, not our OAuth: Shopify picks the store, and the app takes over from there. */}
      {shopifyUrl ? <Button asChild variant="outline" size="icon" className={buttonClass}>
        <a href={shopifyUrl} aria-label={tUi("continueWithShopify")}>
          <ShopifyLogo className="size-5 text-shopify-on-light dark:text-shopify-on-dark" />
          {prominent ? <ProminentLabel name="Shopify" full={fullLabels} /> : null}
        </a>
      </Button> : null}
    </div>
    {start.error && <p className="text-sm text-destructive" role="alert"><UiErrorText message={start.error.message} /></p>}
    {dividerPlacement === "after" ? <div className="pt-2"><SocialDivider label={tUi("orUseEmail")} /></div> : null}
  </div>
}

function resolveShopifyUrl(target: ShopifyLinkTarget | undefined, signInUrl: string | null): string | null {
  switch (target) {
    case "sign-in":
      return signInUrl
    case "install":
      return getShopifyAppListingUrl()
    case undefined:
      return null
  }
}

function socialProviderName(provider: "facebook" | "google"): string {
  switch (provider) {
    case "facebook":
      return "Facebook"
    case "google":
      return "Google"
  }
}

function ProminentLabel({ name, full }: { name: string; full: boolean }) {
  const tUi = useTranslations("Auth.SocialSignInButtons")

  if (!full) return <span className="truncate text-xs sm:text-sm">{name}</span>
  return <>
    <span className="text-xs sm:hidden">{name}</span>
    <span className="hidden sm:inline">{<span key={tUi("continueWithValue0", { value0: String(name) })}>{tUi("continueWithValue0", { value0: String(name) })}</span>}</span>
  </>
}

/** Reserves the social sign-in area while public provider availability loads. */
function SocialSignInButtonsLoader({
  dividerPlacement,
  prominent,
  headingLeading,
  placeholders,
}: {
  dividerPlacement: SocialDividerPlacement
  prominent: boolean
  headingLeading?: ReactNode
  placeholders: number
}) {
  const tUi = useTranslations("Auth.SocialSignInButtons")

  return <div className="space-y-3" aria-busy="true" aria-label={tUi("loadingSignInOptions")}>
    {dividerPlacement === "before" ? <SocialDivider label={tUi("orContinueWith")} /> : null}
    {prominent ? <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
      <div>{headingLeading}</div>
      <p className="text-center text-sm font-medium"><span key={tUi("signUpFasterWith")}>{tUi("signUpFasterWith")}</span></p>
      <div />
    </div> : null}
    <div className={prominent ? PROMINENT_ROW_CLASS : "flex justify-center gap-3"}>
      {Array.from({ length: placeholders }, (_, index) => <div key={index} className={prominent ? "flex h-10 flex-1 items-center justify-center rounded-md border bg-background sm:h-12" : "flex size-11 items-center justify-center rounded-full border bg-background"}>
        <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>)}
    </div>
    {dividerPlacement === "after" ? <div className="pt-2"><SocialDivider label={tUi("orUseEmail")} /></div> : null}
  </div>
}

function SocialDivider({ label }: { label: string }) {
  return <div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />{label}<span className="h-px flex-1 bg-border" /></div>
}

function SocialProviderLogo({ provider }: { provider: "facebook" | "google" }) {
  switch (provider) {
    case "facebook":
      return <svg aria-hidden="true" className="size-5" viewBox="0 0 24 24"><path fill="#1877F2" d="M24 12a12 12 0 1 0-13.875 11.85v-8.38H7.08V12h3.045V9.356c0-3.006 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.513c-1.49 0-1.956.925-1.956 1.874V12h3.328l-.532 3.47h-2.796v8.38A12.004 12.004 0 0 0 24 12Z" /></svg>
    case "google":
      return <svg aria-hidden="true" className="size-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M21.35 12.23c0-.71-.06-1.39-.18-2.05H12v3.88h5.24a4.48 4.48 0 0 1-1.94 2.94v2.51h3.14c1.84-1.7 2.91-4.2 2.91-7.28Z" /><path fill="#34A853" d="M12 21.75c2.63 0 4.84-.87 6.45-2.36l-3.14-2.51c-.87.58-1.98.92-3.31.92-2.54 0-4.69-1.71-5.46-4.01H3.3v2.59A9.75 9.75 0 0 0 12 21.75Z" /><path fill="#FBBC05" d="M6.54 13.79A5.86 5.86 0 0 1 6.24 12c0-.62.11-1.22.3-1.79V7.62H3.3A9.75 9.75 0 0 0 2.25 12c0 1.57.38 3.05 1.05 4.38l3.24-2.59Z" /><path fill="#EA4335" d="M12 6.2c1.43 0 2.71.49 3.72 1.45l2.79-2.79C16.83 3.29 14.63 2.25 12 2.25a9.75 9.75 0 0 0-8.7 5.37l3.24 2.59C7.31 7.91 9.46 6.2 12 6.2Z" /></svg>
  }
}
